import React, { useEffect } from 'react'
import { useToggle } from 'src/hooks'

import { Button, Icon } from '@centivo/react-components'
import { Map, Modal } from 'src/refactor/blocks'
import { ContentSection } from 'src/refactor/structure'
import { Card } from './components/CardSlice'

import {
  aboutData,
  checkMultipleLanguages,
  checkIsVirtualCare,
  checkIsVirtualCareOnly,
  locationData,
  getModalStyle,
} from './functions/practitionerDetailsFunctions'

import {
  Column,
  ContentGrid,
  Divider,
  Grid,
  Row,
  StyledPractitionerDetails,
  RedirectModal,
} from './PractitionerDetailsSlice.styled'

const Section: React.FC<{
  title: string
  isMobileView?: boolean
  firstElm?: boolean
  marginBottom?: string
}> = ({ title, isMobileView = false, firstElm = false, marginBottom }) => {
  return (
    <Row
      isMobileView={isMobileView}
      firstElm={firstElm}
      marginBottom={marginBottom}
    >
      <h2>{title}</h2>
      <Divider isMobileView={isMobileView} aria-hidden />
    </Row>
  )
}

export const PractitionerDetailsSlice: React.FC<{
  formattedName: string
  primarySpecialty: string
  practiceName: string
  lat: number
  long: number
  practiceWebpageURL: string | null
  goBack: () => void
  isMobileView: boolean
  practitionerDetails: any
}> = ({
  formattedName,
  primarySpecialty,
  practiceName,
  lat,
  long,
  practiceWebpageURL,
  goBack,
  isMobileView,
  practitionerDetails,
}) => {
  const [isModalOpen, toggleModal, setIsModalOpen] = useToggle(false)

  const onRedirectUser = () => setIsModalOpen(true)
  const onRequestClose = () => setIsModalOpen(false)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    document.title = 'Centivo Provider Search - Doctor details page'
    document.getElementById('aria-field')?.focus()

    if (isModalOpen) {
      timer = setTimeout(() => {
        const a = document.createElement('a')
        a.setAttribute('href', practiceWebpageURL as string)
        a.setAttribute('target', '_blank')
        a.click()
        setIsModalOpen(false)
      }, 3000)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [isModalOpen, practiceWebpageURL])

  const isVirtualCare = checkIsVirtualCare(
    practitionerDetails.virtualOption
  ) as boolean

  const isVirtualCareOnly = checkIsVirtualCareOnly(
    practitionerDetails.virtualOption
  ) as boolean

  const modalStyle = getModalStyle(isMobileView, true)

  const about = aboutData(practitionerDetails)

  const location = locationData(
    isVirtualCareOnly,
    isVirtualCare,
    practitionerDetails
  )

  const isFullContact =
    Array.isArray(location) &&
    location.filter(data => data.type === 'contact')?.length === 2

  return (
    <StyledPractitionerDetails>
      <ContentSection>
        <ContentGrid>
          <Column>
            <span>
              <button
                className="back-button"
                type="button"
                onClick={goBack}
                aria-label="Tap to go back to the previous page"
              >
                <Icon icon="slice-arrow-left" size="2.5rem" aria-hidden />
              </button>
            </span>
          </Column>
          <Column aria-label={`${formattedName} info`}>
            <div className="title">
              <span className="title-name">
                <h1>{formattedName}</h1>
                {isVirtualCare && (
                  <Icon icon="slice-video" size="40px" aria-hidden />
                )}
              </span>
            </div>
            <span className="specialty-section">{primarySpecialty}</span>
            <Section
              title="About"
              isMobileView={isMobileView}
              firstElm={!!about}
            />
            <Grid>
              {about.map(item => {
                return (
                  <Card
                    key={item.title}
                    show={item.show ?? true}
                    title={item.title}
                    value={
                      item.title === 'Languages'
                        ? checkMultipleLanguages(item.value)
                        : item.value
                    }
                  />
                )
              })}
            </Grid>
            <Section title="Office Location" marginBottom="29px" />
            <Grid>
              <div>
                <Card
                  title={practiceName}
                  value={location}
                  isFullContact={!!isFullContact}
                />
              </div>

              {!isVirtualCareOnly ? (
                <div>
                  <Map
                    pins={[
                      {
                        id: 'practitioner',
                        latitude: lat,
                        longitude: long,
                      },
                    ]}
                    style={{
                      width: 'auto',
                      height: isMobileView ? 200 : 275,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      borderRadius: '20px',
                    }}
                  />
                </div>
              ) : (
                <div className="spacer" />
              )}
            </Grid>

            {practiceWebpageURL ? (
              <div>
                <Section
                  title="Learn more about the doctor"
                  isMobileView={isMobileView}
                />
                <div className="website-link">
                  <button onClick={onRedirectUser} type="button">
                    Visit the website
                    <Icon
                      icon="slice-external-link"
                      size="1.25rem"
                      aria-hidden
                    />
                  </button>
                </div>
              </div>
            ) : null}
          </Column>
        </ContentGrid>

        <Modal
          style={modalStyle}
          isOpen={isModalOpen}
          aria-hidden={!isModalOpen}
          role="alertdialog"
          aria-modal="true"
        >
          <RedirectModal>
            <div>
              <h1 className="modal-title">See you later!</h1>
              <img src="/logos/slice-centivo-mobile.svg" alt="centivo logo" />
            </div>
            <p>You are leaving Centivo and will be automatically redirected.</p>
            <Button buttonType="secondary" onClick={onRequestClose}>
              Cancel
            </Button>
          </RedirectModal>
        </Modal>
      </ContentSection>
    </StyledPractitionerDetails>
  )
}
